import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  Boxes,
  Briefcase,
  Car,
  Package,
  UserPlus,
  ClipboardCheck,
  UserCheck,
  Calendar,
  CheckSquare,
  ClipboardList,
  Building2,
  UserCog,
  ListChecks,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Plane,
  Settings,
  ShieldAlert,
  Users,
  UsersRound,
} from 'lucide-react'
import { useAuth, useLogout } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { NotificationBell } from '@/components/NotificationBell'
import { useIsDeptHead } from '@/hooks/useRecruitment'
import type { UserRole } from '@/lib/types'

type Nav = { to: string; label: string; icon: typeof LayoutDashboard; adminOnly?: boolean; roles?: UserRole[]; deptHead?: boolean }

const NAV: Nav[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/requests', label: 'Requests', icon: ClipboardList },
  { to: '/approvals', label: 'My approvals', icon: ListChecks },
  { to: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager', 'handler'] },
  { to: '/team', label: 'HRAD Team', icon: UsersRound },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/meeting-rooms', label: 'Meeting Rooms', icon: Briefcase },
  { to: '/incidents', label: 'Incidents', icon: ShieldAlert },
  { to: '/trainings', label: 'Trainings', icon: FileText },
  { to: '/travel', label: 'Travel', icon: Plane },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/recruitment', label: 'Recruitment', icon: UserPlus, roles: ['admin', 'manager', 'handler'], deptHead: true },
  { to: '/onboarding', label: 'Onboarding', icon: ClipboardCheck, roles: ['admin', 'manager', 'handler'] },
  { to: '/my-onboarding', label: 'My onboarding', icon: UserCheck, roles: ['employee'] },
  { to: '/assets', label: 'Assets', icon: Boxes, roles: ['admin', 'manager', 'handler'] },
  { to: '/assets/mine', label: 'My assets', icon: Package },
  { to: '/vehicles', label: 'Vehicles', icon: Car },
  { to: '/departments', label: 'Departments', icon: Building2, adminOnly: true },
  { to: '/users', label: 'Users and roles', icon: UserCog, adminOnly: true },
  { to: '/reports', label: 'Reports', icon: FileText },
  { to: '/settings', label: 'Settings', icon: Settings },
]

function NavItems({ items, onNavigate }: { items: Nav[]; onNavigate?: () => void }) {
  return (
    <nav className="flex-1 space-y-1 overflow-y-auto p-3">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/assets'}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
              isActive
                ? 'bg-wegen-50 text-wegen-700 font-medium'
                : 'text-slate-700 hover:bg-slate-100',
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  )
}

export function Layout() {
  const { user } = useAuth()
  const logout = useLogout()
  const { isDeptHead } = useIsDeptHead()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen])
  const visible = NAV.filter((n) => {
    if (n.adminOnly && user?.role !== 'admin') return false
    if (!n.roles) return true
    if (user && n.roles.includes(user.role)) return true
    return Boolean(n.deptHead && isDeptHead)
  })

  return (
    <div className="flex h-full min-h-screen overflow-x-hidden bg-slate-50">
      <aside className="hidden w-60 shrink-0 border-r border-slate-200 bg-white md:flex md:flex-col">
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <span className="text-base font-semibold tracking-tight text-slate-900">
            WeGen <span className="text-wegen-600">HRAD</span>
          </span>
        </div>
        <NavItems items={visible} />
        <div className="border-t border-slate-200 p-3 text-xs text-slate-600">
          <div className="truncate font-medium text-slate-900">
            {user?.full_name ?? user?.email}
          </div>
          <div className="mt-0.5 capitalize">{user?.role}</div>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            data-testid="nav-backdrop"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-white shadow-xl"
          >
            <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
              <span className="text-base font-semibold tracking-tight text-slate-900">
                WeGen <span className="text-wegen-600">HRAD</span>
              </span>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavItems items={visible} onNavigate={() => setDrawerOpen(false)} />
            <div className="border-t border-slate-200 p-3 text-xs text-slate-600">
              <div className="truncate font-medium text-slate-900">{user?.full_name ?? user?.email}</div>
              <div className="mt-0.5 capitalize">{user?.role}</div>
              <button
                type="button"
                onClick={() => logout.mutate()}
                className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 md:h-16 md:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav"
              onClick={() => setDrawerOpen(true)}
              className="rounded p-1.5 text-slate-600 hover:bg-slate-100 md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="text-sm font-semibold tracking-tight text-slate-900 md:hidden">
              WeGen <span className="text-wegen-600">HRAD</span>
            </span>
          </div>
          <NotificationBell />
        </header>
        {user?.must_change_password ? (
          <div role="alert" className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 md:px-6">
            You are using a temporary password.{' '}
            <Link to="/account/password" className="font-medium underline">
              Change your password
            </Link>{' '}
            to continue securely.
          </div>
        ) : null}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
